import { defineMessages } from "../define";

/** Public landing page (/landing): marketing copy for the pizza quality checker. */
export default defineMessages({
  en: {
    // hero
    "landing.hero.titleLead": "Pizza quality control,",
    "landing.hero.titleHighlight": "reimagined",
    "landing.hero.description":
      "An AI-powered quality checker for pizzerias and restaurant chains. Computer vision, neural networks, and smart cameras make sure every pizza leaves the kitchen exactly as it should.",
    "landing.hero.viewDashboard": "View dashboard",
    // stats
    "landing.stats.title": "Proven results",
    "landing.stats.subtitle": "Measurable gains in quality control and operational efficiency",
    "landing.stats.consistency.label": "Quality consistency",
    "landing.stats.consistency.description": "Improvement in pizza quality",
    "landing.stats.speed.label": "Inspection speed",
    "landing.stats.speed.value": "3×",
    "landing.stats.speed.description": "Faster than manual inspection",
    "landing.stats.accuracy.label": "AI accuracy",
    "landing.stats.accuracy.description": "Ingredient detection accuracy",
    "landing.stats.efficiency.label": "Efficiency gain",
    "landing.stats.efficiency.description": "Less time spent on manual quality checks",
    // features
    "landing.features.eyebrow": "Advanced technology",
    "landing.features.title": "A complete quality control solution",
    "landing.features.subtitle":
      "Cutting-edge AI, computer vision, and IoT working together to keep every pizza up to standard",
    "landing.features.camera.title": "Smart camera integration",
    "landing.features.camera.description":
      "Every pizza is photographed automatically in high resolution, triggered at exactly the right moment.",
    "landing.features.evaluation.title": "AI-powered evaluation",
    "landing.features.evaluation.description":
      "Neural networks assess the quality of each pizza and check that every ingredient is in place.",
    "landing.features.vision.title": "Computer vision pipeline",
    "landing.features.vision.description":
      "Advanced image processing locates the pizza in the frame and analyzes its quality layer by layer.",
    "landing.features.visualization.title": "Interactive visualization",
    "landing.features.visualization.description":
      "Heatmaps and detected components drawn right over the photo, in real time.",
    "landing.features.analytics.title": "Analytics dashboard",
    "landing.features.analytics.description":
      "Quality metrics, reports, and trend analysis, all in one place.",
    "landing.features.integration.title": "System integration",
    "landing.features.integration.description":
      "Seamless connection to the Biteberry delivery platform and AWS S3 storage.",
    // technology stack
    "landing.tech.title": "Built on modern technology",
    "landing.tech.subtitle": "Enterprise-grade technology for scalability, reliability, and performance",
    "landing.tech.neuralNetworks": "Neural networks",
    "landing.tech.computerVision": "Computer vision",
    "landing.tech.restApis": "REST APIs",
    "landing.tech.smartCamera": "Smart camera",
    "landing.tech.iot": "IoT integration",
    "landing.tech.realTime": "Real-time processing",
    // system in action (screenshot placeholders)
    "landing.showcase.title": "The system in action",
    "landing.showcase.subtitle": "See how our AI pizza quality checks work in real time",
    "landing.showcase.capture.title": "Smart camera capture",
    "landing.showcase.capture.description": "Pizza photos captured in real time",
    "landing.showcase.analysis.title": "AI analysis view",
    "landing.showcase.analysis.description": "Multi-layer quality assessment",
    "landing.showcase.analytics.title": "Quality analytics dashboard",
    "landing.showcase.analytics.description": "In-depth reports and insights",
    "landing.showcase.results.title": "Quality check results",
    "landing.showcase.results.description": "Automatic pass/fail verdicts",
    // call to action
    "landing.cta.title": "Ready to transform your quality control?",
    "landing.cta.description":
      "Experience the future of automated pizza quality checks with an AI solution proven in daily operation.",
    "landing.cta.viewDemo": "View live demo",
    // footer
    "landing.footer.credits": "Developed by Algovectra s.r.o.",
    "landing.footer.tagline": "Advanced AI for quality control in food service",
  },
  cs: {
    // hero
    "landing.hero.titleLead": "Kontrola kvality pizzy",
    "landing.hero.titleHighlight": "nové generace",
    "landing.hero.description":
      "Chytrá kontrola kvality s umělou inteligencí pro pizzerie a sítě restaurací. Počítačové vidění, neuronové sítě a chytré kamery hlídají, aby každá pizza opouštěla kuchyni přesně taková, jaká má být.",
    "landing.hero.viewDashboard": "Otevřít Dashboard",
    // stats
    "landing.stats.title": "Prokazatelné výsledky",
    "landing.stats.subtitle": "Měřitelné zlepšení kontroly kvality i provozní efektivity",
    "landing.stats.consistency.label": "Vyrovnaná kvalita",
    "landing.stats.consistency.description": "Zlepšení kvality pizzy",
    "landing.stats.speed.label": "Rychlost kontroly",
    "landing.stats.speed.value": "3×",
    "landing.stats.speed.description": "Rychlejší než ruční kontrola",
    "landing.stats.accuracy.label": "Přesnost AI",
    "landing.stats.accuracy.description": "Přesnost rozpoznání surovin",
    "landing.stats.efficiency.label": "Úspora času",
    "landing.stats.efficiency.description": "Méně času stráveného ruční kontrolou kvality",
    // features
    "landing.features.eyebrow": "Pokročilé technologie",
    "landing.features.title": "Kompletní řešení kontroly kvality",
    "landing.features.subtitle":
      "Špičková umělá inteligence, počítačové vidění a IoT v jednom systému, díky kterému každá pizza splní vaše standardy",
    "landing.features.camera.title": "Napojení chytrých kamer",
    "landing.features.camera.description":
      "Každá pizza se automaticky vyfotí ve vysokém rozlišení – a to přesně ve správnou chvíli.",
    "landing.features.evaluation.title": "Vyhodnocení pomocí AI",
    "landing.features.evaluation.description":
      "Neuronové sítě posoudí kvalitu každé pizzy a zkontrolují, že na ní nechybí žádná surovina.",
    "landing.features.vision.title": "Počítačové vidění",
    "landing.features.vision.description":
      "Pokročilé zpracování obrazu najde pizzu na snímku a rozebere její kvalitu vrstvu po vrstvě.",
    "landing.features.visualization.title": "Interaktivní vizualizace",
    "landing.features.visualization.description":
      "Heatmapy a rozpoznané komponenty pizzy vykreslené přímo do fotky v reálném čase.",
    "landing.features.analytics.title": "Analytický Dashboard",
    "landing.features.analytics.description":
      "Metriky kvality, přehledy a vývoj v čase – vše na jednom místě.",
    "landing.features.integration.title": "Napojení na vaše systémy",
    "landing.features.integration.description":
      "Hladké propojení s rozvozovou platformou Biteberry a úložištěm AWS S3.",
    // technology stack
    "landing.tech.title": "Postaveno na moderních technologiích",
    "landing.tech.subtitle": "Technologie podnikové třídy pro škálovatelnost, spolehlivost a výkon",
    "landing.tech.neuralNetworks": "Neuronové sítě",
    "landing.tech.computerVision": "Počítačové vidění",
    "landing.tech.restApis": "REST API",
    "landing.tech.smartCamera": "Chytrá kamera",
    "landing.tech.iot": "Integrace IoT",
    "landing.tech.realTime": "Zpracování v reálném čase",
    // system in action (screenshot placeholders)
    "landing.showcase.title": "Systém v akci",
    "landing.showcase.subtitle": "Podívejte se, jak naše kontrola kvality pizzy s AI funguje v reálném čase",
    "landing.showcase.capture.title": "Pořízení fotky chytrou kamerou",
    "landing.showcase.capture.description": "Fotky pizzy v reálném čase",
    "landing.showcase.analysis.title": "Vizualizace analýzy AI",
    "landing.showcase.analysis.description": "Vícevrstvé posouzení kvality",
    "landing.showcase.analytics.title": "Dashboard kvality",
    "landing.showcase.analytics.description": "Podrobné přehledy a poznatky",
    "landing.showcase.results.title": "Výsledky kontroly kvality",
    "landing.showcase.results.description": "Automatický verdikt OK/NEOK",
    // call to action
    "landing.cta.title": "Chcete mít kvalitu každé pizzy pod kontrolou?",
    "landing.cta.description":
      "Vyzkoušejte budoucnost automatické kontroly kvality pizzy – s řešením na bázi AI, které se už osvědčilo v každodenním provozu.",
    "landing.cta.viewDemo": "Vyzkoušet živé demo",
    // footer
    "landing.footer.credits": "Vyvinula Algovectra s.r.o.",
    "landing.footer.tagline": "Pokročilá AI pro kontrolu kvality v gastronomii",
  },
});
